import { Link } from 'react-router-dom'
import { useCartStore } from '@/store/cartStore'
import { formatPrice } from '@/lib/utils'
import Button from '../ui/Button'
import { X, ShoppingCart, Minus, Plus } from 'lucide-react'

interface CartDrawerProps {
  isOpen: boolean
  onClose: () => void
}

export default function CartDrawer({ isOpen, onClose }: CartDrawerProps) {
  const { 
    items, 
    updateQuantity, 
    removeItem, 
    getTotalPrice, 
    getTotalItems 
  } = useCartStore()

  const totalItems = getTotalItems()
  const totalPrice = getTotalPrice()

  if (!isOpen) return null

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-50"
        onClick={onClose}
      />
      
      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-xl z-50 transform transition-transform duration-300">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-neutral-200">
          <h2 className="text-lg font-semibold text-neutral-800">
            Sepetim ({totalItems})
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-neutral-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex flex-col h-full">
          {items.length === 0 ? (
            /* Empty State */
            <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
              <ShoppingCart className="w-16 h-16 text-neutral-300 mb-4" />
              <h3 className="text-lg font-medium text-neutral-600 mb-2">
                Sepetiniz Boş
              </h3>
              <p className="text-neutral-500 mb-6">
                Lezzetli böreklerimizi keşfetmek için ürünler sayfasını ziyaret edin.
              </p>
              <Link to="/urunler" onClick={onClose}>
                <Button>Ürünleri Gör</Button>
              </Link>
            </div>
          ) : (
            <>
              {/* Items */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {items.map((item) => (
                  <div key={item.product.id} className="flex items-center gap-3 p-3 bg-neutral-50 rounded-lg">
                    <img
                      src={item.product.image || 'https://images.unsplash.com/photo-1571115764595-644a1f56a55c?w=60&h=60&fit=crop'}
                      alt={item.product.name}
                      className="w-12 h-12 object-cover rounded-lg"
                    />
                    
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-sm text-neutral-800 truncate">
                        {item.product.name}
                      </h4>
                      <p className="text-xs text-neutral-600">
                        {formatPrice(item.product.price)}
                      </p>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => updateQuantity(item.product.id, item.quantity - 1)}
                        className="w-6 h-6 flex items-center justify-center rounded border border-neutral-300 hover:bg-neutral-100"
                      >
                        <Minus className="w-3 h-3" />
                      </button>
                      
                      <span className="w-8 text-center text-sm font-medium">
                        {item.quantity}
                      </span>
                      
                      <button
                        onClick={() => updateQuantity(item.product.id, item.quantity + 1)}
                        className="w-6 h-6 flex items-center justify-center rounded border border-neutral-300 hover:bg-neutral-100"
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>

                    <button
                      onClick={() => removeItem(item.product.id)}
                      className="p-1 text-red-600 hover:bg-red-50 rounded"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>

              {/* Footer */}
              <div className="border-t border-neutral-200 p-4 space-y-4">
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-neutral-800">Toplam:</span>
                  <span className="font-bold text-lg text-primary-600">
                    {formatPrice(totalPrice)}
                  </span>
                </div>
                
                <div className="space-y-2">
                  <Link to="/sepet" onClick={onClose}>
                    <Button variant="outline" className="w-full">
                      Sepeti Görüntüle
                    </Button>
                  </Link>
                  <Link to="/siparis" onClick={onClose}>
                    <Button className="w-full">
                      Siparişi Tamamla
                    </Button>
                  </Link>
                </div>

                <p className="text-xs text-center text-neutral-500">
                  💳 Kapıda ödeme • 🚚 Ücretsiz teslimat
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  )
}